/**
 * Keeps the active softphone version aligned with the
 * `communicator-softphone-v2` feature flag, which is its single controller:
 *
 * - flag enabled  -> new softphone (v2)
 * - flag disabled -> legacy softphone
 *
 * There is no user setting for this any more. The resolved value is persisted
 * per user so non-React code can read it synchronously
 * (`SoftphoneCoordinator.readPreferenceSync`), and the app reloads whenever it
 * changes so the session starts with a single SIP registration.
 */

import { COMMUNICATOR_SOFTPHONE_V2 } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { useAreFeatureFlagsIdentified } from 'Hooks/featureFlags/useFeatureFlagsIdentified';
import { MobXProviderContext } from 'mobx-react';
import { useSoftphoneCoordinatorStore } from 'Modules/softphone/store';
import { providerLogger as logger } from 'Modules/softphone/utils/logger';
import { useContext, useEffect } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';

/**
 * Resolve the softphone version from the feature flag value.
 *
 * Only an explicit `false` sends a user to the legacy softphone; a missing or
 * not yet resolved flag keeps the new softphone, which is the default.
 *
 * @param flagValue Value of the `communicator-softphone-v2` flag.
 * @returns Whether the new softphone should be active.
 */
export const resolveIsNewSoftphoneEnabled = (flagValue?: boolean): boolean =>
  flagValue !== false;

export const useSoftphoneVersionSync = (): void => {
  const { person } = useContext<RootStoreProps>(MobXProviderContext);
  const personId = person?.loggedInPersonId;

  const { [COMMUNICATOR_SOFTPHONE_V2]: softphoneV2EnabledFF } =
    useFeatureFlags<boolean>([COMMUNICATOR_SOFTPHONE_V2]);

  // Flags evaluated for the anonymous bootstrap context must not be acted on:
  // reloading on those values would fight the values for the real user.
  const areFeatureFlagsIdentified = useAreFeatureFlagsIdentified();

  const coordinatorStore = useSoftphoneCoordinatorStore(personId);
  const syncWithFeatureFlag = coordinatorStore(
    (state) => state.syncWithFeatureFlag
  );

  useEffect(() => {
    if (!areFeatureFlagsIdentified) {
      return;
    }

    const isNewSoftphoneEnabled =
      resolveIsNewSoftphoneEnabled(softphoneV2EnabledFF);

    logger.debug('Syncing softphone version with feature flag', {
      personId,
      softphoneV2EnabledFF,
      isNewSoftphoneEnabled,
    });

    syncWithFeatureFlag(isNewSoftphoneEnabled);
  }, [
    areFeatureFlagsIdentified,
    softphoneV2EnabledFF,
    syncWithFeatureFlag,
    personId,
  ]);
};
